import Colour from 'components/utils/Colour'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { getThemeClassName } from 'lib/theme/themeUtils'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  LUX_PLUS_EXPERIENCE_DISCOUNT_ALL_OFFERS_URL,
  LUX_PLUS_EXPERIENCE_DISCOUNT_BECOME_MEMBER_URL,
} from '../constants/luxPlusExperienceDiscountTakeoverConstants'
import LuxPlusExperienceDiscountCalendarLogo from './LuxPlusExperienceDiscountCalendarLogo'
import { useShowExperienceDiscountBanner } from '../hooks/useShowExperienceDiscountBanner'

interface Props {
  withCta?: boolean
}

function LuxPlusExperienceDiscountOfferBanner({ withCta }: Props) {
  const intl = useIntl()
  const shouldShow = useShowExperienceDiscountBanner()
  const isLuxPlusMember = useAppSelector(getCanRedeemLuxPlusBenefits)

  if (!shouldShow) {
    return null
  }

  const ctaUrl = isLuxPlusMember ? LUX_PLUS_EXPERIENCE_DISCOUNT_ALL_OFFERS_URL : LUX_PLUS_EXPERIENCE_DISCOUNT_BECOME_MEMBER_URL
  const viewOffersLabel = intl.formatMessage({
    id: 'EO9N1ZgriQ',
    defaultMessage: 'View offers',
    description: 'CTA button for LuxPlus+ members to view experience discount offers',
  })
  const becomeMemberLabel = intl.formatMessage({
    id: 'iSfgAsL54V',
    defaultMessage: 'Become a member',
    description: 'CTA button for non-members to join LuxPlus+ for experience discounts',
  })
  const ctaText = isLuxPlusMember ? viewOffersLabel : becomeMemberLabel

  return <Group
    direction="horizontal"
    gap={12}
    verticalAlign="center"
    className={getThemeClassName({ mobile: 'inverse' })}
  >
    <LuxPlusExperienceDiscountCalendarLogo size="S" />
    <CSSBreakpoint max="mobile">
      <BodyText variant="large">
        {withCta && <FormattedMessage
          id="cHsBb9nexx"
          defaultMessage='<bold>LuxPlus<colorPlus>+</colorPlus></bold> members save 10% off all Experiences until 5 Jan when using promo code "EXP10LP" at checkout. <link>{ctaText}</link>'
          description="Mobile banner copy with CTA link for LuxPlus+ experience discount promotion"
          values={{
            colorPlus: (chunks) => <Colour value="highlight-lux-plus">{chunks}</Colour>,
            bold: (chunks) => <b>{chunks}</b>,
            ctaText,
            link: (chunks) => <TextLink to={ctaUrl} wrap="no-wrap">{chunks}</TextLink>,
          }}
        />}
        {!withCta && <FormattedMessage
          id="G1flH+B/kN"
          defaultMessage='<bold>LuxPlus<colorPlus>+</colorPlus></bold> members save 10% off all Experiences until 5 Jan when using promo code "EXP10LP" at checkout.'
          description="Mobile banner copy for LuxPlus+ experience discount promotion"
          values={{
            colorPlus: (chunks) => <Colour value="highlight-lux-plus">{chunks}</Colour>,
            bold: (chunks) => <b>{chunks}</b>,
          }}
        />}
      </BodyText>
    </CSSBreakpoint>
    <CSSBreakpoint min="tablet">
      <BodyText variant="large">
        <FormattedMessage
          id="91yj1eciz/"
          defaultMessage='<bold>LuxPlus<colorPlus>+</colorPlus></bold> members save more – 10% off all Experiences until 5 Jan when using promo code "EXP10LP" at checkout.'
          description="Desktop banner copy for LuxPlus+ experience discount promotion"
          values={{
            colorPlus: (chunks) => <Colour value="highlight-lux-plus">{chunks}</Colour>,
            bold: (chunks) => <b>{chunks}</b>,
          }}
        />
      </BodyText>
    </CSSBreakpoint>
    {withCta && (
      <CSSBreakpoint min="tablet" className={getThemeClassName({ mobile: 'inverse' })}>
        <TextButton kind="secondary" size="small" to={ctaUrl}>
          {ctaText}
        </TextButton>
      </CSSBreakpoint>
    )}
  </Group>
}

export default LuxPlusExperienceDiscountOfferBanner
